import {
    NetworkError,
    generateRouteDefinition,
    makeCanonicalUrl,
} from '@jet-app/music-classical/utilities';
import {
    makeLibraryTracksPageIntent,
    type LibraryTracksPageIntent,
} from './LibraryTracksPageIntent';
import type { IntentController } from '@jet/environment/dispatching';
import type { RouteProvider } from '@jet/environment/routing';
import type { ObjectGraph } from '@jet/environment/dependencies';
import type { LibraryTracksPage } from '@jet-app/music-classical/models/pages/library/tracks';
import { IntentKind } from '@jet-app/music-classical/intents';
import {
    requestViewPage,
    requestViewPageWithQuery,
} from '@jet-app/music-classical/api/request-view-page';
import { renderLibraryTracksPage } from './renderLibraryTracksPage';
import {
    isEmptyPage,
    renderEmptyPage,
} from '@jet-app/music-classical/utilities/transforms/empty-view';
import { pageSortByChangeEventTrigger } from '../../common/page-invalidation-rules';
import { requiresSignIn } from '../utils/requires-sign-in';

const canonicalUrlRule = {
    path: '/library/tracks',
    query: [],
};

export const LibraryTracksPageIntentController: IntentController<LibraryTracksPageIntent> &
    RouteProvider = requiresSignIn({
    $intentKind: IntentKind.LIBRARY_TRACKS_PAGE,

    routes: generateRouteDefinition<LibraryTracksPageIntent>(
        canonicalUrlRule,
        makeLibraryTracksPageIntent,
    ),

    async perform(
        intent: LibraryTracksPageIntent,
        objectGraph: ObjectGraph,
    ): Promise<LibraryTracksPage> {
        const canonicalURL = makeCanonicalUrl(
            canonicalUrlRule,
            intent,
            objectGraph,
        );

        let apiResponse;

        if (intent.metadata?.url) {
            apiResponse = await requestViewPageWithQuery(
                intent.metadata.url,
                objectGraph,
            );
        } else {
            apiResponse = await requestViewPage(
                `favorites/tracks`,
                objectGraph,
            );
        }

        if (!apiResponse?.body) {
            throw NetworkError.noContent();
        }

        if (isEmptyPage(apiResponse)) {
            return renderEmptyPage<LibraryTracksPage>(
                canonicalURL,
                apiResponse,
                objectGraph,
            );
        }

        const page = renderLibraryTracksPage(
            apiResponse,
            objectGraph,
            canonicalURL,
        ) as unknown as LibraryTracksPage;

        return {
            ...page,
            invalidationRules: {
                eventTriggers: [pageSortByChangeEventTrigger(intent)],
            },
        };
    },
});
